import { MatchState, TennisPoint, MatchStats } from './types';

export function createInitialMatchState(
  player1Name: string = '选手 1 (Joy Club)',
  player2Name: string = '选手 2',
  isNoAd: boolean = false
): MatchState {
  return {
    player1Name,
    player2Name,
    currentSetIndex: 0,
    sets: [{ player1Games: 0, player2Games: 0 }],
    currentGame: {
      serverPoints: '0',
      receiverPoints: '0',
      serverTiebreakPoints: 0,
      receiverTiebreakPoints: 0,
    },
    currentServer: 1,
    isTiebreak: false,
    isSuperTiebreak: false,
    isMatchOver: false,
    winner: null,
    servingSide: 'deuce',
    isNoAd,
    stats: {
      aces: [0, 0],
      doubleFaults: [0, 0],
      firstServeIn: [0, 0],
      firstServeTotal: [0, 0],
      winners: [0, 0],
      unforcedErrors: [0, 0],
      breakPointsWon: [0, 0],
      breakPointsTotal: [0, 0],
      netPointsWon: [0, 0],
      netPointsTotal: [0, 0],
      totalPointsWon: [0, 0],
      rallyShortWon: [0, 0],
      rallyMediumWon: [0, 0],
      rallyLongWon: [0, 0],
    },
    pointHistory: [],
  };
}

const POINT_SEQUENCE: TennisPoint[] = ['0', '15', '30', '40'];

/**
 * Adds a point to player 1 or 2
 */
export function scorePoint(
  state: MatchState,
  winner: 1 | 2,
  pointType: 'winner' | 'unforced_error' | 'forced_error' | 'ace' | 'double_fault' = 'winner',
  rallyLength: 'short' | 'medium' | 'long' = 'short'
): { nextState: MatchState; callout: string } {
  if (state.isMatchOver) {
    return { nextState: state, callout: '比赛已结束 (Match Finished)' };
  }

  const nextState: MatchState = JSON.parse(JSON.stringify(state));
  const server = nextState.currentServer;
  const isServerWinner = winner === server;
  let callout = '';

  // Update stats
  const winnerIdx = winner === 1 ? 0 : 1;
  const loserIdx = winner === 1 ? 1 : 0;
  nextState.stats.totalPointsWon[winnerIdx]++;

  if (pointType === 'ace') {
    nextState.stats.aces[server === 1 ? 0 : 1]++;
    nextState.stats.winners[winnerIdx]++;
  } else if (pointType === 'double_fault') {
    nextState.stats.doubleFaults[server === 1 ? 0 : 1]++;
    nextState.stats.unforcedErrors[loserIdx]++;
  } else if (pointType === 'winner') {
    nextState.stats.winners[winnerIdx]++;
  } else if (pointType === 'unforced_error') {
    nextState.stats.unforcedErrors[loserIdx]++;
  }

  if (rallyLength === 'short') nextState.stats.rallyShortWon[winnerIdx]++;
  else if (rallyLength === 'medium') nextState.stats.rallyMediumWon[winnerIdx]++;
  else nextState.stats.rallyLongWon[winnerIdx]++;

  // Handle Tiebreak Scoring
  if (nextState.isTiebreak || nextState.isSuperTiebreak) {
    const targetPoints = nextState.isSuperTiebreak ? 10 : 7;
    if (isServerWinner) {
      nextState.currentGame.serverTiebreakPoints++;
    } else {
      nextState.currentGame.receiverTiebreakPoints++;
    }

    const p1Tb = server === 1 ? nextState.currentGame.serverTiebreakPoints : nextState.currentGame.receiverTiebreakPoints;
    const p2Tb = server === 1 ? nextState.currentGame.receiverTiebreakPoints : nextState.currentGame.serverTiebreakPoints;
    const totalTbPoints = p1Tb + p2Tb;

    // Check tiebreak win condition (must lead by 2)
    const winningPlayer = p1Tb >= targetPoints && p1Tb - p2Tb >= 2 ? 1 : (p2Tb >= targetPoints && p2Tb - p1Tb >= 2 ? 2 : null);

    if (winningPlayer) {
      // Won tiebreak and set
      const currentSet = nextState.sets[nextState.currentSetIndex];
      if (winningPlayer === 1) currentSet.player1Games++;
      else currentSet.player2Games++;
      currentSet.tiebreakScore = `${Math.min(p1Tb, p2Tb)}`;

      callout = `局点！抢七获胜！${winningPlayer === 1 ? nextState.player1Name : nextState.player2Name} 赢得本盘！`;
      checkSetWin(nextState, winningPlayer);
    } else {
      // Rotate serve in tiebreak: Player 1 serves 1 point, then each serves 2 points
      if (totalTbPoints % 2 === 1) {
        nextState.currentServer = nextState.currentServer === 1 ? 2 : 1;
      }
      nextState.servingSide = totalTbPoints % 2 === 0 ? 'deuce' : 'ad';
      callout = `${p1Tb} - ${p2Tb}`;
    }

    return { nextState, callout };
  }

  // Handle Regular Game Scoring
  let sPts = nextState.currentGame.serverPoints;
  let rPts = nextState.currentGame.receiverPoints;

  if (isServerWinner) {
    if (sPts === '0') sPts = '15';
    else if (sPts === '15') sPts = '30';
    else if (sPts === '30') sPts = '40';
    else if (sPts === '40') {
      if (rPts === '40') {
        if (nextState.isNoAd) {
          // Sudden death win on deuce
          return completeGame(nextState, server);
        } else {
          sPts = 'AD'; // Advantage Server
        }
      } else if (rPts === 'AD') {
        rPts = '40'; // Back to Deuce
      } else {
        // Game won by server
        return completeGame(nextState, server);
      }
    } else if (sPts === 'AD') {
      return completeGame(nextState, server);
    }
  } else {
    // Receiver won point
    if (rPts === '0') rPts = '15';
    else if (rPts === '15') rPts = '30';
    else if (rPts === '30') rPts = '40';
    else if (rPts === '40') {
      if (sPts === '40') {
        if (nextState.isNoAd) {
          // Sudden death win on deuce
          const receiver = server === 1 ? 2 : 1;
          return completeGame(nextState, receiver);
        } else {
          rPts = 'AD'; // Advantage Receiver
        }
      } else if (sPts === 'AD') {
        sPts = '40'; // Back to Deuce
      } else {
        // Game won by receiver (Break)
        const receiver = server === 1 ? 2 : 1;
        nextState.stats.breakPointsWon[receiver === 1 ? 0 : 1]++;
        return completeGame(nextState, receiver);
      }
    } else if (rPts === 'AD') {
      const receiver = server === 1 ? 2 : 1;
      nextState.stats.breakPointsWon[receiver === 1 ? 0 : 1]++;
      return completeGame(nextState, receiver);
    }
  }

  nextState.currentGame.serverPoints = sPts;
  nextState.currentGame.receiverPoints = rPts;

  // Toggle serving side: Deuce -> Ad -> Deuce
  nextState.servingSide = nextState.servingSide === 'deuce' ? 'ad' : 'deuce';

  // Construct standard tennis callout
  if (sPts === '40' && rPts === '40') {
    callout = '平分 (Deuce)';
  } else if (sPts === 'AD') {
    callout = '发球占先 (Advantage In)';
  } else if (rPts === 'AD') {
    callout = '接发占先 (Advantage Out / Break Point)';
  } else if (sPts === rPts) {
    callout = `${sPts} 平 (${sPts}-All)`;
  } else {
    callout = `${sPts} - ${rPts}`;
  }

  return { nextState, callout };
}

function completeGame(state: MatchState, gameWinner: 1 | 2): { nextState: MatchState; callout: string } {
  const currentSet = state.sets[state.currentSetIndex];
  if (gameWinner === 1) {
    currentSet.player1Games++;
  } else {
    currentSet.player2Games++;
  }

  // Reset game score
  state.currentGame = {
    serverPoints: '0',
    receiverPoints: '0',
    serverTiebreakPoints: 0,
    receiverTiebreakPoints: 0,
  };
  state.servingSide = 'deuce';

  // Check if set is won or tiebreak reached
  const p1G = currentSet.player1Games;
  const p2G = currentSet.player2Games;

  if (p1G >= 6 && p1G - p2G >= 2) {
    return checkSetWin(state, 1);
  } else if (p2G >= 6 && p2G - p1G >= 2) {
    return checkSetWin(state, 2);
  } else if (p1G === 6 && p2G === 6) {
    state.isTiebreak = true;
    // Server rotates for tiebreak
    state.currentServer = state.currentServer === 1 ? 2 : 1;
    const callout = `局数 6-6 平！进入决胜局抢七 (Tiebreak)！`;
    return { nextState: state, callout };
  } else {
    // Normal game won, swap server
    state.currentServer = state.currentServer === 1 ? 2 : 1;
    const winnerName = gameWinner === 1 ? state.player1Name : state.player2Name;
    const totalGames = p1G + p2G;
    const changeSideNotice = totalGames % 2 === 1 ? ' · 双方交换场地 (Change Ends)' : '';
    const callout = `局！${winnerName} 获胜！比分 ${p1G}-${p2G}${changeSideNotice}`;
    return { nextState: state, callout };
  }
}

function checkSetWin(state: MatchState, setWinner: 1 | 2): { nextState: MatchState; callout: string } {
  state.isTiebreak = false;
  const setsWonByP1 = state.sets.filter((s, idx) => {
    if (idx === state.currentSetIndex) return setWinner === 1;
    return s.player1Games > s.player2Games;
  }).length;

  const setsWonByP2 = state.sets.filter((s, idx) => {
    if (idx === state.currentSetIndex) return setWinner === 2;
    return s.player2Games > s.player1Games;
  }).length;

  // Best of 3 sets match
  if (setsWonByP1 >= 2 || setsWonByP2 >= 2) {
    state.isMatchOver = true;
    state.winner = setsWonByP1 >= 2 ? 1 : 2;
    const winnerName = state.winner === 1 ? state.player1Name : state.player2Name;
    return {
      nextState: state,
      callout: `比赛结束！恭喜 ${winnerName} 赢得全场比赛！(Game, Set and Match!)`,
    };
  } else {
    // Proceed to next set
    state.currentSetIndex++;
    state.sets.push({ player1Games: 0, player2Games: 0 });
    state.currentServer = state.currentServer === 1 ? 2 : 1;
    const winnerName = setWinner === 1 ? state.player1Name : state.player2Name;
    return {
      nextState: state,
      callout: `盘！${winnerName} 赢得本盘！总盘数 ${setsWonByP1}-${setsWonByP2}，进入下一盘！`,
    };
  }
}
